import React from 'react';
import Row from 'components/UIKit/Flex/Row';
import Column from 'components/UIKit/Flex/Column';
import Card from 'components/UIKit/Card';
import ExternalLink from 'components/Links/ExternalLink';
import { InternalLink } from 'components/Links';
import ReviewButtonContainer from 'containers/workflow/ReviewButtonContainer';
import { Tooltip } from 'components/UIKit/Tooltip';
import OpenButtonContainer from 'containers/workflow/OpenButtonContainer';
import SubmitButtonContainer from 'containers/workflow/SubmitButtonContainer';
import { SECONDARY_RED } from 'style/colors';

const styles = {
  topBullet: {
    textTransform: 'uppercase',
    fontSize: '1,3rem',
    marginLeft: '16px',
    fontWeight: 'bold',
  },
};

const WarningInstruction = ({ warningCount }) => (
  <li>
    <span>
      <strong>
        {`Please review ${warningCount} warnings `}
      </strong>
      in your
    </span>

    <span>{' project\'s '}</span>

    <InternalLink to="qc" withProjectPath>QC Report</InternalLink>

    <span>
      {' before putting the data into review status. The report details '}
    </span>

    <ExternalLink href="https://gdc.cancer.gov">
      unresolved errors and warnings
    </ExternalLink>

    <span>
      {' for the current unsubmitted data. It is recommended that you resolve warnings before going into the review stage, since data is locked during review unless the project is explicitly re-opened.'}
    </span>
  </li>
);

const CriticalInstruction = ({ criticalCount, warningCount }) => (
  <li>
    <span>
      <strong>
        <span style={{ color: SECONDARY_RED }}>
          {`Please resolve ${criticalCount} CRITICAL errors `}
        </span>
        {warningCount > 0 && `and review ${warningCount} warnings `}
      </strong>
      {'in your project\'s '}
    </span>

    <InternalLink to="qc" withProjectPath>QC Report</InternalLink>

    <span>
      {' before putting the data into review status. The report details '}
    </span>

    <ExternalLink href="https://docs.gdc.cancer.gov/Data_Submission_Portal/Users_Guide/Data_Submission_Process/#pre-harmonization-checklist">
      unresolved errors and warnings
    </ExternalLink>

    <span>
      {' for the current unsubmitted data. You must resolve CRITICAL errors before going into the review stage.'}
    </span>
  </li>
);

const isDisabled = ({
  isFetching,
  params,
  projectInfo,
}) => isFetching
  || !projectInfo.submission_enabled
  || projectInfo.request_submission
  || !(params.program && params.project);

const ReviewAndSubmit = ({
  params,
  projectInfo,
  widgets: {
    qcErrors,
    qcErrors: {
      data: {
        WARNING: warningCount,
        CRITICAL: criticalCount,
      },
      isFetching,
    },
  },
}) => (
  <Row style={{ marginTop: '1rem' }}>
    <Column data-testid="table-review-and-submit-your-workspace" style={{ flex: 3 }}>
      <Row style={styles.topBullet}>2. Review and Submit your Workspace Data to the GDC</Row>
      <Card>
        <ol type="A">
          {criticalCount > 0
            ? (
              <CriticalInstruction
                criticalCount={criticalCount}
                warningCount={warningCount}
                />
            )
            : warningCount > 0 && <WarningInstruction warningCount={warningCount} />}

          <li>
            Review your data before you submit it to the GDC.
            The review will prevent users from uploading data in the workspace.
          </li>

          <li>
            {'Click the "Request Harmonization" button to submit validated data to the GDC for processing once the review is completed. Processing includes harmonization and high level data generation, when applicable. All validated data in the project workspace will be processed. For information on GDC processing, please read '}
            <ExternalLink href="https://gdc.cancer.gov/submit-data/data-submission-processes-and-tools">
              GDC Data Processing Software and Algorithms
            </ExternalLink>
          </li>
        </ol>

        <Row>
          {!projectInfo.in_review && (
            <ReviewButtonContainer
              isDisabled={isDisabled({
                params, projectInfo, criticalCount, isFetching,
              })}
              />
          )}

          {projectInfo.in_review && !projectInfo.request_submission && (
            <Row>
              <OpenButtonContainer params={params} isDisabled={!projectInfo.submission_enabled}>
              Re-open
              </OpenButtonContainer>
              <SubmitButtonContainer
                isDisabled={isDisabled({ params, projectInfo })}
                params={params}
                qcErrors={qcErrors}
                >
                {projectInfo.request_submission ? 'HARMONIZATION REQUESTED' : isDisabled({ params, projectInfo }) ? 'SUBMIT DISABLED' : 'REQUEST HARMONIZATION'}
              </SubmitButtonContainer>
            </Row>
          )}

          {projectInfo.request_submission && (
            <Row>
              <Tooltip Component="Harmonization Requested - Project in review by the GDC">
                <SubmitButtonContainer
                  isDisabled={isDisabled({ params, projectInfo })}
                  params={{}}
                  qcErrors={qcErrors}
                  >
                  {projectInfo.request_submission ? 'HARMONIZATION REQUESTED' : isDisabled({ params, projectInfo }) ? 'SUBMIT DISABLED' : 'REQUEST HARMONIZATION'}
                </SubmitButtonContainer>
              </Tooltip>
            </Row>
          )}
        </Row>
      </Card>
    </Column>
  </Row>
);

export default ReviewAndSubmit;
